import { action, extendObservable, toJS } from 'mobx';
import { orderBy } from 'lodash';
import FetchService from '../../../core/services/FetchService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';

class UnitItemListService {
  fetch = new FetchService();
  submit = new FetchService();

  constructor() {
    extendObservable(this, {
      items: [],
      unitId: null,
      reorderSubmitting: false,
      selectable: false,
      selectedRows: [],
    });
  }

  init = action((unit) => {
    this.items = [];
    this.unitId = unit.id;
    this.unitType = unit.type.id;
    this.load();
  });

  load = action(() => {
    this.fetch
      .fetch({
        url: `/unit-types/${this.unitType}/item-types`,
      })
      .then(() => {
        if (this.fetch.data) {
          const unitTypes = toJS(this.fetch.data);

          this.fetch
            .fetch({
              url: `/units/${this.unitId}/items`,
            })
            .then(() => {
              if (this.fetch.data) {
                this.items = orderBy(this.fetch.data, ['order', 'group'], ['asc', 'asc']).map((unitItem) => ({
                  ...unitItem,
                  item: {
                    ...unitItem.item,
                    invalidItemType: !unitTypes.find((unitType) => unitType.id === unitItem.item.type.id),
                  },
                }));

                this.reorderSubmitting = false;
              } else {
                this.items = [];
              }
            });
        } else {
          this.items = [];
        }
      });
  });

  toggleSelectable = action(() => {
    this.selectable = !this.selectable;
  });

  handleRemove = action((unitItem) => {
    ConfirmationDialogService.show(
      'Delete Draft Item',
      `Would you like to the draft item with data "Order: ${unitItem.order}, Group: ${
        ['', 'Default', 'First review', 'Second review'][unitItem.group]
      }, Text: ${unitItem.item.text}, Justification: ${
        unitItem.item.draftCreationReason
      }"? This action cannot be undone.`,
      () => {
        this.submit
          .fetch({
            url: `/units/${unitItem.unit}/items/${unitItem.item.id}`,
            method: 'delete',
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification('Draft items successfully deleted', 'success');
              this.load();
            }
            if (this.submit.error) {
              NotificationService.addNotification(this.submit.error || 'Error to delete draft', 'error');
            }
          });
      }
    );
  });

  handlePublish = action((unitItem) => {
    ConfirmationDialogService.show(
      'Publish Draft Item',
      `Do you really want to publish the draft item with data "Order: ${unitItem.order}, Group: ${
        ['', 'Default', 'First review', 'Second review'][unitItem.group]
      }, Text: ${unitItem.item.text}"?`,
      () => {
        this.submit
          .fetch({
            method: 'patch',
            url: `/units/${unitItem.unit}/items/${unitItem.item.id}/publish`,
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification('Draft item published successfully', 'success');
              this.load();
            }
            if (this.submit.error) {
              NotificationService.addNotification(this.submit.error || `Error to publish draft item`, 'error');
            }
          });
      }
    );
  });

  handleUnpublish = action((unitItem) => {
    ConfirmationDialogService.show(
      'Unpublish Item',
      `Do you really want to unpublish the item with data "Order: ${unitItem.order}, Group: ${
        ['', 'Default', 'First review', 'Second review'][unitItem.group]
      }, Text: ${unitItem.item.text}"?`,
      () => {
        this.submit
          .fetch({
            method: 'patch',
            url: `/units/${unitItem.unit}/items/${unitItem.item.id}/unpublish`,
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification('Draft item unpublished successfully', 'success');
              this.load();
            }
            if (this.submit.error) {
              NotificationService.addNotification(this.submit.error || `Error to publish draft item`, 'error');
            }
          });
      }
    );
  });

  handleOrderOrGroupChange = action((unitItem, order, group) => {
    this.submit
      .fetch({
        url: `/units/${unitItem.unit}/items/${unitItem.item.id}`,
        method: 'put',
        body: {
          order,
          group,
        },
      })
      .then(() => {
        this.load();
      });
  });

  handleAutoReorder = action((startIndex, sortAction) => {
    this.reorderSubmitting = true;

    const reorderPromises = [];
    this.items.forEach((unitItem, index) => {
      const body = {
        group: unitItem.group,
        order: unitItem.order,
      };

      if (sortAction === 'ADD_LINE' && index >= startIndex) {
        body.order = unitItem.order + 1;
      } else if (sortAction === 'REMOVE_LINE' && index >= startIndex) {
        body.order = unitItem.order - 1;
      }

      reorderPromises.push(
        this.submit.fetch({
          url: `/units/${unitItem.unit}/items/${unitItem.item.id}`,
          method: 'put',
          body,
        })
      );
    });

    Promise.all(reorderPromises).then(() => this.load());
  });

  handleSelectRows = action((selectedRows) => {
    this.selectedRows = selectedRows;
  });

  getCheckboxProps = (unitItem) => {
    if (
      unitItem.item.status === 'DRAFT' ||
      this.items.some((ui) => ui.item.parent && ui.item.parent === unitItem.item.id)
    ) {
      return {
        disabled: true,
      };
    }
    return false;
  };
}

const unitItemListService = new UnitItemListService();

export default unitItemListService;
